import { useMemo, type ReactNode } from 'react';
import { toast as sonner } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

/**
 * Thin wrapper around sonner that keeps the existing useToast() API so
 * call-sites don't need to migrate. Premium look-and-feel comes from the
 * sonner Toaster (mounted once by ToastProvider).
 */
type ToastFn = (title: string, body?: string) => void;

type ToastContextValue = {
  toast: (tone: 'success' | 'error' | 'info', title: string, body?: string) => void;
  success: ToastFn;
  error: ToastFn;
  info: ToastFn;
};

const ctx: ToastContextValue = {
  toast: (tone, title, body) => {
    if (tone === 'success') sonner.success(title, { description: body });
    else if (tone === 'error') sonner.error(title, { description: body });
    else sonner.info(title, { description: body });
  },
  success: (title, body) => sonner.success(title, { description: body }),
  error: (title, body) => sonner.error(title, { description: body }),
  info: (title, body) => sonner.info(title, { description: body }),
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}

export function useToast(): ToastContextValue {
  // No real context needed — sonner is module-global. Memo just to be a hook.
  return useMemo(() => ctx, []);
}
